import React, { useContext, useEffect } from 'react';
import { ContentBox } from '../ContentBox/ContentBox';
import { useGetLoanDocByLoan } from 'services/loan/querys';
import { TaskOpenModalContext } from 'scenes/Private/Private';
import { LoanDocState, TaskState } from 'gql/graphql';
import { buildDocItems, Item } from '@common/utils/helpers/buildDocItem';
import { TasksContext } from 'scenes/Private/Dashboard/Dashboard';

interface IProps {
  loanSelected: any;
  anotherDocs: any;
}

// Configuración de estados de documentos
export const DOCUMENT_STATES = {
  insurance: {
    [LoanDocState.Pending]: 'Not Contacted',
    [LoanDocState.Contacted]: 'Contacted',
    [LoanDocState.Received]: 'Received',
    [LoanDocState.Approved]: 'Approved',
    default: 'Not Contacted'
  },
  title: {
    [LoanDocState.Pending]: 'Not Requested',
    [LoanDocState.Requested]: 'Requested',
    [LoanDocState.Verified]: 'Verified',
    [LoanDocState.Cleared]: 'Cleared to close',
    default: 'Not Requested'
  }
};

// Configuración de tareas opcionales
const OPTIONAL_TASKS_CONFIG = {
  construction_budget: {
    condition: (loanSelected: any, task: any) => !!task,
    label: 'Construction Budget',
    taskKey: 'construction'
  },
  purchase: {
    condition: (loanSelected: any) => loanSelected?.purpose === 'Purchase',
    label: 'Purchase contract',
    taskKey: 'purchase'
  },
  payoff: {
    condition: (loanSelected: any) => loanSelected?.purpose === 'Refinance',
    label: 'Payoff letter',
    taskKey: 'payoff'
  }
};

export const BoxAdditionalDocuments: React.FC<IProps> = ({ loanSelected, anotherDocs }: IProps) => {

  const tasks: any = useContext(TasksContext);

  const { setHandler: openTask }: any = useContext(TaskOpenModalContext);

  const { data: loanDocs, reexecute: getLoanDocByLoan } = useGetLoanDocByLoan({
    variables: { loanId: loanSelected._id },
    pause: true
  });

  useEffect(() => {
    if (!loanDocs) {
      getLoanDocByLoan({ requestPolicy: 'network-only' });
    }
  }, []);

  // Utilidades para encontrar documentos y tareas
  const findDoc = (key: string) => loanDocs?.find(item => item.key === key);
  const findTask = (key: string) => tasks.find((item: any) => item.key === key);

  // Documentos y tareas principales
  const docs = {
    insurance: findDoc('insurance'),
    title: findDoc('title')
  };

  const tasksByKey = {
    insurance: findTask('insurance'),
    title_company: findTask('title_company'),
    construction_budget: findTask('construction_budget'),
    purchase: findTask('purchase'),
    payoff: findTask('payoff')
  };

  const getLabel = (docType: 'insurance' | 'title', docState?: LoanDocState): string => {
    const states = DOCUMENT_STATES[docType];
    return docState && states[docState as keyof typeof states]
      ? states[docState as keyof typeof states]
      : states.default;
  };

  const createDocumentState = (docType: 'insurance' | 'title', doc: any, task: any, taskKey: string) => {

    const isDocApproved = docType === 'insurance'
      ? doc?.state === LoanDocState.Approved
      : doc?.state === LoanDocState.Verified || doc?.state === LoanDocState.Cleared;
    // transform state for insurance task
    const transformStateInsurance = !doc && task?.metadata?.contactedApproved && task?.state === TaskState.Approved ? TaskState.Review : task?.state;
    // Condicion especial para title cuando escoge la opcion de no contar con una compañia se toma como approved
    const isTaskApproved = docType === 'title'
      ? task?.state === TaskState.Approved || (task?.state === TaskState.Review && task?.metadata?.titleCompany === undefined)
      : transformStateInsurance === TaskState.Approved;

    return {
      label: getLabel(docType, doc?.state),
      isDocApproved,
      isTaskApproved,

      get customValue() {
        if ((isTaskApproved && !isDocApproved) || doc?.hidden) {
          return <i>{`Available after ${docType} is verified`}</i>;
        }
        if (isTaskApproved && isDocApproved) {
          return undefined;
        }
        const stateToEvaluate = docType === 'insurance' ? transformStateInsurance : task?.state;

        if (stateToEvaluate === TaskState.Review) {
          return <span onClick={() => openTask(taskKey)}>{"In review ->"}</span>;
        }
        if (stateToEvaluate === TaskState.RequestError) {
          return <span onClick={() => openTask(taskKey)}>{"In error ->"}</span>;
        }
        if (stateToEvaluate === TaskState.Draft || stateToEvaluate === TaskState.Incomplete) {
          return <span onClick={() => openTask(taskKey)}>{"View task ->"}</span>;
        }
        return undefined;
      },

      get shouldShowDownload() {
        return isTaskApproved && isDocApproved && !doc?.hidden;
      }
    };
  };

  // Estados de documentos principales
  const documentStates = {
    insurance: createDocumentState('insurance', docs.insurance, tasksByKey.insurance, 'insurance'),
    title: createDocumentState('title', docs.title, tasksByKey.title_company, 'title_company')
  };

  // Generar value para tareas opcionales
  const getOptionalTaskValue = (task: any): JSX.Element => {
    if (task?.state === TaskState.Review) {
      return <span onClick={() => openTask(task.key)}>{`In ${task.state.toLowerCase()} ->`}</span>;
    }
    if (task?.state === TaskState.RequestError) {
      return <span onClick={() => openTask(task.key)}>{`In error ->`}</span>;
    }
    if (task?.state === TaskState.Approved) {
      if (task.key === 'purchase') {
        const urlFileApproved = task.responses[0]?.files?.filter((file: any) => file.state === 'approved')?.[0]?.url;
        return (
          <span
            onClick={() => {
              if (urlFileApproved) {
                window.open(`${urlFileApproved}?preview=true&app=borrower`, '_blank');
              } else {
                openTask(task.key);
              }
            }}
          >
            {"Download ->"}
          </span>
        );
      }
      return <span onClick={() => openTask(task.key)}>{"Approved ->"}</span>;
    }
    return <span onClick={() => openTask(task.key)}>{"View task ->"}</span>;
  };

  // Construcción del contenido
  const additionalDocumentsContent: Item[] = [
    // Documentos opcionales dinámicos
    ...Object.entries(OPTIONAL_TASKS_CONFIG)
      .filter(([key, config]) => {
        const task = tasksByKey[key as keyof typeof tasksByKey];
        return config.condition(loanSelected, task);
      })
      .map(([key, config]) => {
        const task = tasksByKey[key as keyof typeof tasksByKey];
        return {
          field: config.label,
          value: getOptionalTaskValue(task)
        };
      }),

    // Insurance
    ...buildDocItems(
      "Insurance",
      docs.insurance,
      { state: documentStates.insurance.label },
      "",
      documentStates.insurance.customValue,
      documentStates.insurance.shouldShowDownload
    ),

    // Title  
    ...buildDocItems(
      "Title",
      docs.title,
      { state: documentStates.title.label },
      "",
      documentStates.title.customValue,
      documentStates.title.shouldShowDownload
    )
  ];

  // Agregar documentos adicionales externos
  if (anotherDocs) {
    anotherDocs.forEach((anotherDoc: any) => {
      const file = anotherDoc.files[0];
      additionalDocumentsContent.push({
        field: anotherDoc.label,
        value: <span onClick={() => window.open(`${file.url}?preview=true&app=borrower`, '_blank')}>{"View file ->"}</span>
      });
    });
  }

  //Agregar Loan Documents creados desde processing team
  loanDocs?.filter(doc => !doc.key).forEach((doc: any) => {
    additionalDocumentsContent.push(...buildDocItems(
      doc.name,
      doc,
      {},
      "",
      undefined,
      true
    ));
  });

  return (
    <ContentBox
      label={'Additional documents'}
      content={additionalDocumentsContent}
      isBoxAdditionalDocuments={true}
    />
  );
};